import { BodyRegular, BodySmall, Card, Headline6 } from 'components/Elements';
import React from 'react';
import { breakpoints, sizes, theme } from 'theme';
import strings from '../../../../strings/strings.json';
import { BuildMethod, GenerateErrorReason } from 'models/Activity';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import ValidateIcon from 'assets/icons/indicators/ValidateIcon';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PreviewIcon from 'assets/icons/controls/PreviewIcon';
import { StepId } from 'pages/build-course/CourseBuilderPage';
import RefreshIcon from 'assets/icons/RefreshIcon';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { useNavigate } from 'react-router-dom';
import { PercentageProgressBar } from 'components/progressBar/ProgressBar';
import ActivityTypeLabel from '../ActivityTypeLabel';
import { ModuleItem } from './ModuleItem';
import { styled } from 'styled-components';
import { IconSize } from 'assets/icons/icon-sizes';
import useModifyRequest from 'hooks/useModifyRequest';
import { GenerateItem, getStatus, Status } from './TaskGenerateItem';
import { GenerateButton } from './GenerateButton';
import { BuildCourseContext } from 'contextProviders/EditCourseContext';

export type ModuleItemProps = {
    module: ModuleItem;
    courseId: string;
    setTaskIdToReview: React.Dispatch<React.SetStateAction<string>>;
};

export function ModuleListItem({ module, courseId, setTaskIdToReview }: ModuleItemProps): JSX.Element {
    return (
        <ModuleCard>
            <Headline6>{`${strings.courseAIGeneratedContentComponent.moduleTitlePrefix} ${module.moduleOrder}: ${module.moduleTitle}`}</Headline6>
            <ActivityGrid>
                {module.generateItems.map((content: GenerateItem, taskIndex) => {
                    return (
                        <ActivityCardComponent
                            key={taskIndex}
                            content={content}
                            courseId={courseId}
                            setTaskIdToReview={setTaskIdToReview}
                        />
                    );
                })}
            </ActivityGrid>
        </ModuleCard>
    );
}

type ActivityCardComponentProps = {
    content: GenerateItem;
    courseId: string;
    setTaskIdToReview: React.Dispatch<React.SetStateAction<string>>;
};

function ActivityCardComponent({ content, courseId, setTaskIdToReview }: ActivityCardComponentProps): JSX.Element {
    const navigate = useNavigate();
    const context = React.useContext(BuildCourseContext);

    const { modifyData: generateContent, loading: generatingContent } = useModifyRequest(
        `generate-content-statuses/${content.id}/retry`,
        'POST',
    );

    async function startGenerateClick() {
        await generateContent({});
        await context.refetchCourse();
    }

    let approvedLabel = <></>;
    let buttonRow = <></>;

    const status = getStatus(content);

    switch (status) {
        case Status.APPROVED:
            approvedLabel = (
                <ApprovedLabel>
                    <ApprovedText $bold>{strings.courseAIGeneratedContentComponent.approvedStatus}</ApprovedText>
                    <ValidateIcon />
                </ApprovedLabel>
            );
            buttonRow = (
                <SecondaryButton
                    title={strings.courseAIGeneratedContentComponent.viewButton.title}
                    aria={strings.courseAIGeneratedContentComponent.viewButton.aria}
                    icon={<PreviewIcon size={IconSize.MEDIUM} />}
                    fullwidth
                    onClick={() =>
                        navigate(`/build-course/${courseId}`, {
                            state: {
                                stepId: 'courseStructureAndContent' as StepId,
                                editActivityId: content.activityId,
                            },
                        })
                    }
                />
            );
            break;
        case Status.GENERATED:
            buttonRow = (
                <PrimaryButton
                    title={strings.courseAIGeneratedContentComponent.reviewButton.title}
                    aria={strings.courseAIGeneratedContentComponent.reviewButton.aria}
                    onClick={() => setTaskIdToReview(content.id)}
                    icon={<PreviewIcon size={IconSize.MEDIUM} colour={theme.cardBackground} />}
                    fullwidth
                />
            );
            break;
        case Status.ERRORED:
            buttonRow = (
                <ErrorContainer>
                    <ErrorRow>
                        <ErrorNotifierSlim description={getFriendlyError(content.error)} />
                    </ErrorRow>
                    <ButtonsContainer>
                        <SecondaryButton
                            title={strings.courseAIGeneratedContentComponent.retryButton.title}
                            aria={strings.courseAIGeneratedContentComponent.retryButton.aria}
                            onClick={async () => await startGenerateClick()}
                            submitting={generatingContent}
                            icon={<RefreshIcon />}
                            fullwidth
                        />
                        <ErrorRow>{strings.courseAIGeneratedContentComponent.retryError}</ErrorRow>
                    </ButtonsContainer>
                </ErrorContainer>
            );
            break;
        case Status.STARTED:
            const expectedMinsToComplete = 3;
            const completionPercentage = getCompletionPercentage(expectedMinsToComplete, content.dateGenerateStart!);
            buttonRow = (
                <EstimatedTimeBase>
                    <PercentageProgressBar
                        label={`${strings.courseAIGeneratedContentComponent.estimatedTime}: ${expectedMinsToComplete} ${strings.courseAIGeneratedContentComponent.min}`}
                        percentage={completionPercentage}
                    />
                </EstimatedTimeBase>
            );
            break;
        case Status.CREATED:
        default:
            buttonRow = (
                <GenerateButton
                    generatedType={content.generateType}
                    onClick={async () => await startGenerateClick()}
                    disableButton={generatingContent}
                />
            );
            break;
    }

    const taskType = content.taskBuildMethod ? getTaskTypeLabel(content.taskBuildMethod) : '';

    return (
        <ActivityCard>
            <BodyRegular $bold>{content.activityTitle}</BodyRegular>
            <TypeAndButton>
                <ActivityTypeLabelRow>
                    <ActivityTypeLabel activityType={content.activityType} suffix={taskType} />
                    {approvedLabel}
                </ActivityTypeLabelRow>
                {buttonRow}
            </TypeAndButton>
        </ActivityCard>
    );
}

function getTaskTypeLabel(method: BuildMethod): string {
    switch (method) {
        case BuildMethod.AUDIO:
            return strings.courseAIGeneratedContentComponent.taskTypeLabel.audio;
        case BuildMethod.VIDEO:
            return strings.courseAIGeneratedContentComponent.taskTypeLabel.video;
        case BuildMethod.TEXT_AND_IMAGE:
        default:
            return strings.courseAIGeneratedContentComponent.taskTypeLabel.text;
    }
}

const ErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

const ModuleCard = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

const ActivityTypeLabelRow = styled.div`
    display: flex;
    justify-content: space-between;
`;

const TypeAndButton = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

const ActivityCard = styled(Card)`
    flex: 0 0 calc(25% - ${sizes.spacingSm});
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${sizes.spacingMd};
    min-width: 300px;
    max-width: 300px;

    @media (max-width: ${breakpoints.lg}) {
        flex: 0 0 calc(100% - ${sizes.spacingSm});
        min-width: unset;
        max-width: unset;
    }
`;

const ActivityGrid = styled.div`
    display: flex;
    gap: ${sizes.spacingSm};
    flex-wrap: wrap;
`;

const ApprovedLabel = styled.div`
    display: flex;
    gap: ${sizes.spacingSm};
`;

const ApprovedText = styled(BodySmall)`
    white-space: nowrap;
`;

const EstimatedTimeBase = styled.div`
    display: flex;
    width: 100%;
`;

const ButtonsContainer = styled.div`
    display: flex;
    gap: ${sizes.spacingMd};
    justify-content: space-between;
    flex: 45%;

    @media (max-width: ${breakpoints.md}) {
        flex-direction: column;
        width: 100%;
    }
`;

const ErrorRow = styled.div`
    display: flex;
    justify-content: space-between;
`;

function getCompletionPercentage(expectedMinsToComplete: number, dateCreated: Date): number {
    const tenMinsAfterDateCreated = new Date(dateCreated);
    tenMinsAfterDateCreated.setMinutes(tenMinsAfterDateCreated.getMinutes() + expectedMinsToComplete);

    const differenceInMinutes = (tenMinsAfterDateCreated.getTime() - new Date().getTime()) / (1000 * 60);

    const minsElapsed = expectedMinsToComplete - differenceInMinutes;

    const percentageComplete =
        minsElapsed >= expectedMinsToComplete ? 99 : Math.round((minsElapsed / expectedMinsToComplete) * 100);

    return percentageComplete;
}

function getFriendlyError(error: GenerateErrorReason): string {
    let friendlyError = '';

    switch (error) {
        case GenerateErrorReason.MISSING_ENV_VAR_POLLY_REGION:
        case GenerateErrorReason.MISSING_ENV_VAR_REGION:
        case GenerateErrorReason.MISSING_ENV_VAR_DOCUMENTS_REGION:
        case GenerateErrorReason.MISSING_ENV_VAR_DOCUMENTS_BUCKET_NAME:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.serverError;
            break;
        case GenerateErrorReason.ACTIVITY_NOT_TASK:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.activityNotTask;
            break;
        case GenerateErrorReason.TASK_NO_CONTENT:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.taskNoContent;
            break;
        case GenerateErrorReason.CONTENT_ALREADY_GENERATED:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.contentAlreadyGenerated;
            break;
        case GenerateErrorReason.POLLY_ERROR:
        case GenerateErrorReason.POLLY_RESPONSE_NO_AUDIO:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.pollyError;
            break;
        case GenerateErrorReason.UPLOAD_AUDIO_ERROR:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.uploadAudioError;
            break;
        case GenerateErrorReason.UPDATE_STATUS_ERROR:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.updateStatusError;
            break;
        case GenerateErrorReason.NONE:
        default:
            return '';
    }

    return `${strings.courseAIGeneratedContentComponent.generateContentErrors.prefix} ${friendlyError}`;
}
